<template>
  <div class="container-fluid">
    <ol class="breadcrumb">
      <li><router-link :to="{ name: 'dashboard' }">Dashboard</router-link></li>
      <li class="active">Security Events</li>
    </ol>

    <div class="row">
      <div class="col-sm-6">
        <date-range-picker @daterange-change="dateRangeChange"></date-range-picker>
      </div>
      <div class="col-sm-6">
        <h5>{{ dateRangeFormat(dateStart) }} - {{ dateRangeFormat(dateEnd) }}</h5>
      </div>
    </div>

    <!-- <p>{{ aggregateStats }}</p> -->
    <!-- <p>{{ serialStats }}</p> -->
    <!-- <p>{{ statKeys }}</p> -->
    <!-- <p>{{ proxyProtocolStats }}</p> -->
    <!-- <p>{{ columns }} {{ tableData }}</p> -->

    <form class="form-inline">
      <div class="form-group">
        <div class="checkbox">
          <label class="checkbox-inline"><input v-model="showDetails" type="checkbox" :value="showDetails">Show Details</label>
        </div>
      </div>

      <div v-if="showDetails" class="form-group">
        <div v-for="protocol in protocols" class="checkbox">
          <label class="checkbox-inline"><input v-model="selectedProtocols" type="checkbox" :value="protocol">{{ protocol }}</label>
        </div>
      </div>
    </form>

    <div id="people">
      <v-client-table :data="tableData" :columns="columns" :options="options"><v-client-table>
    </div>

  </div>
</template>

<style scoped>
.checkbox-inline {
  margin-right: 1em;
}
input[type="checkbox"] {
  margin-right: 0.25em;
}
</style>

<script>
import _ from 'underscore'
import moment from 'moment'
import * as mutationTypes from '../store/mutation-types'
import C from '../constants'
import DateRangePicker from './DateRangePicker'
import SerialLinkTemplate from '../templates/serialLink.jsx'

const PROTOCOLS = [ 'HTTP', 'SMTP', 'FTP', 'POP3', 'IMAP' ]

export default {
  components: {
    DateRangePicker
  },

  data () {
    return {
      details: {},
      proxyProtocolStats: {},

      protocols: PROTOCOLS,
      selectedProtocols: [ 'HTTP' ],
      showDetails: false,

      options: {
        perPage: 1000,
        perPageValues: [ 100, 500, 1000 ],
        templates: {
          serial: SerialLinkTemplate
        }
        // see the options API
      },

      aggregateBy: {
        'Scanned Bytes': this.statProtocols('totalBytesRead'),
        'Connections': this.statProtocols('totalConnections'),
        'AV Malware': this.statProtocols('blockedVirus'),
        'AI Malware': this.statProtocols('cylanceAvMalware'),
        'AI Clean': this.statProtocols('cylanceAvClean'),
        'Sandbox Malware': this.statProtocols('sandboxAvHashClean'),
        'Sandbox Clean': this.statProtocols('sandboxAvHashMalware'),
        'Sandbox Submitted': this.statProtocols('sandboxAvPayloadSubmit'),
        'Web Filter': _.flatten([
          this.statProtocols('blockedUrls'),
          this.statProtocols('blockedWfUrls'),
          this.statProtocols('warnedWfUrls'),
          this.statProtocols('blockedCMUrls'),
          this.statProtocols('warnedCMUrls')
        ])
      }
    }
  },

  mounted () {
    this.fetch()
  },

  computed: {
    dateRange: {
      get () {
        return this.$store.state.filterDateRange.dateRange
      },

      set (value) {
        this.$store.commit(mutationTypes.FILTER_DATE_RANGE, value)
      }
    },

    dateStart () {
      return moment(this.dateRange[0], C.M_FMT_MS).utc()
    },

    dateEnd () {
      return moment(this.dateRange[1], C.M_FMT_MS).utc()
    },
    columns () {
      var columns = [ 'serial', 'customer', 'city', 'country' ]
      return this.showDetails ? columns.concat(this.statKeys) : columns.concat(_.keys(this.aggregateBy))
    },
    tableData () {
      var data = this.showDetails ? this.serialStats : this.aggregateStats

      return _.map(data, (stats, key) => {
        var obj = {
          serial: key,
          customer: this.serialCustomer(key),
          city: key in this.details ? this.details[key].latestTrack.location.city : '',
          country: key in this.details ? this.details[key].latestTrack.location.country_name : ''
        }

        if (this.showDetails) {
          _.each(this.statKeys, (statKey) => {
            obj[statKey] = statKey in stats ? stats[statKey] : 0
          })
        } else {
          _.extend(obj, stats)
        }

        return obj
      })
    },

    statKeys () {
      var re = new RegExp(this.selectedProtocols.join('|'))

      return _.filter(_.keys(this.proxyProtocolStats), (item) => {
        if (this.selectedProtocols.length === 0) {
          return false
        }
        return item.match(re)
      }).map(this.columnNameFixup)
    },

    serialStats () {
      var serialStats = {}

      // group the statistics by serial, reducing by stat value for each stat date
      _.each(this.proxyProtocolStats, (date, statKey) => {
        statKey = this.columnNameFixup(statKey)
        _.each(date, (stats) => {
          _.each(stats, (val, serial) => {
            if (!(serial in serialStats)) {
              serialStats[serial] = {}
            }
            if (!(statKey in serialStats[serial])) {
              serialStats[serial][statKey] = 0
            }
            serialStats[serial][statKey] += Number(val)
          })
        })
      })

      return serialStats
    },

    // aggregate stats for the detailed view
    // each aggregateBy key is a sum of the stats in the value list
    aggregateStats () {
      return _.mapObject(this.serialStats, (stat) => {
        var obj = {}
        // console.log('AGG stat', stat)
        _.each(this.aggregateBy, (val, aggregateKey) => {
          var total = 0
          _.each(val, (statKey) => {
            total += stat[statKey] || 0
          })
          obj[aggregateKey] = total
        })
        return obj
      })
    }
  },

  methods: {
    fetch () {
      var details = this.$apiWsoc.fetchDetails(null, this.dateStart.format('x'), this.dateEnd.format('x'))
      var proxyProtocolStats = this.$apiWsoc.fetchProxyProtocolStats(this.dateStart.format('x'), this.dateEnd.format('x'))

      this.$store.commit(mutationTypes.LOADER_LOADING)
      Promise.all([details, proxyProtocolStats])
      .then(([detailResult, protocolStatsResult]) => {
        // console.log('fetched', trackIpsResult.result)

        this.details = detailResult
        this.proxyProtocolStats = protocolStatsResult.result

        this.$store.commit(mutationTypes.LOADER_NOT_LOADING)
      })
      .catch(error => {
        console.log('error fetching security events', error)
        this.$store.commit(mutationTypes.LOADER_NOT_LOADING)
      })
    },

    dateRangeFormat (date) {
      return moment(date).format(C.M_FMT_READABLE_WEEKDAY)
    },

    dateRangeChange (e) {
      this.dateRange = e
    },

    serialCustomer (serial) {
      if (!(serial in this.details)) {
        return ''
      }

      if (!('details' in this.details[serial]) || this.details[serial].details.length === 0) {
        return ''
      }

      return this.details[serial].details[0].customer
    },

    statProtocols (statName) {
      return PROTOCOLS.map((protocol) => {
        return protocol + ' ' + statName
      })
    },

    // change 'HTTP.blockedVirus' to 'HTTP blockedVirus' to allow column names to wrap
    columnNameFixup (columnName) {
      return columnName.replace(/\./, ' ')
    }
  },

  watch: {
    dateRange: function () {
      this.fetch()
    }
  }
}
</script>



// WEBPACK FOOTER //
// ReportSecurityEvent.vue?64c1e325